import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { formatInteger } from '../common/formatHelper';
import { cn } from '../common/utils';
import { Table } from '../components/table/Table';
import TagVelocityCategory from '../components/TagVelocityCategory';
import { SKUData } from './sku-profiling';
import { useSKU } from './SKUContext';

interface SKUListViewProps {
  filteredSKUs?: SKUData[];
}

const SKUListView: React.FC<SKUListViewProps> = ({ filteredSKUs }) => {
  const { t } = useTranslation('app');
  const { selectedSKU, setSelectedSKU } = useSKU();

  // Sort SKUs by ABC rank and then by order line count
  const sortedSKUs = useMemo(() => {
    const skusToSort = filteredSKUs || [];
    return [...skusToSort]
      .filter(sku => !selectedSKU || sku.sku === selectedSKU)
      .sort((a, b) => {
        const rankOrder = { 'A+': 0, A: 1, B: 2, C: 3 };
        if (rankOrder[a.rank] !== rankOrder[b.rank]) {
          return rankOrder[a.rank] - rankOrder[b.rank];
        }
        return b.order_line_count - a.order_line_count;
      });
  }, [filteredSKUs, selectedSKU]);

  return (
    <div className="flex flex-col">
      <Table>
        <thead>
          <tr>
            <th className="p-2 text-left">{t`SKU`}</th>
            <th className="p-2 text-left">{t`ABC Rank`}</th>
            <th className="p-2 text-left">{t`XYZ Rank`}</th>
            <th className="p-2 text-left">{t`Combined Rank`}</th>
            <th className="p-2 text-left">{t`Order Lines`}</th>
            <th className="p-2 text-left">{t`Quantity`}</th>
            <th className="p-2 text-left">{t`Lines Accuracy`}</th>
            <th className="p-2 text-left">{t`Quantity Accuracy`}</th>
            <th className="p-2 text-left">{t`Average Lines/Day`}</th>
            <th className="p-2 text-left">{t`Average Quantity/Day`}</th>
          </tr>
        </thead>
        <tbody>
          {sortedSKUs.map((sku, index) => (
            <tr
              key={`sku-row-${sku.sku}-${index}`}
              onClick={() => setSelectedSKU(sku.sku)}
              className={cn(
                'hover:bg-app-panel-dark/30 cursor-pointer transition-colors',
                selectedSKU === sku.sku ? 'bg-app-panel-dark/20' : '',
              )}
            >
              <td key={`sku-${sku.sku}-sku`} className="p-2">
                {sku.sku}
              </td>
              <td key={`sku-${sku.sku}-rank`} className="p-2">
                <TagVelocityCategory rank={sku.rank} />
              </td>
              <td key={`sku-${sku.sku}-xyz`} className="p-2">
                {sku.xyz_rank}
              </td>
              <td key={`sku-${sku.sku}-combined`} className="p-2">
                {sku.combined_rank}
              </td>
              <td key={`sku-${sku.sku}-lines`} className="p-2">
                {formatInteger(sku.order_line_count)}
              </td>
              <td key={`sku-${sku.sku}-quantity`} className="p-2">
                {formatInteger(sku.quantity_ordered)}
              </td>
              <td
                key={`sku-${sku.sku}-lines-acc`}
                className="p-2"
              >{`${(sku.accuracy?.lines || 0).toFixed(1)}%`}</td>
              <td
                key={`sku-${sku.sku}-quantity-acc`}
                className="p-2"
              >{`${(sku.accuracy?.quantity || 0).toFixed(1)}%`}</td>
              <td key={`sku-${sku.sku}-avg-lines`} className="p-2">
                {formatInteger(sku.average_lines_per_day)}
              </td>
              <td key={`sku-${sku.sku}-avg-quantity`} className="p-2">
                {formatInteger(sku.average_quantity_per_day)}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

export default SKUListView;
